import { useCallback, useMemo, useState } from "react";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import type { ColumnVisibilityItem } from "providers/Auth";
import { LeadSavedView, LeadViewColumns, LeadViewFilters } from "../../types";
import { useLeadSavedViews } from "../../hooks/useLeadSavedViews";
import { normalizeViewFilters } from "../../utils/savedViewState";
import { getViewColumns } from "../../utils/columnVisibility";
import { LEADS_COLUMNS_TAB_KEY } from "../../constants";

const DUPLICATE_NAME_MESSAGE = "A view with this name already exists.";

// Detect the backend's name-collision 422. Prefer the structured code
// (body { error: "name_taken" | "validation_failed", ... }); fall back to the
// human message ("A view with this name already exists") so it still resolves
// to the inline field error if the code shape differs from what we expect.
const isDuplicateNameError = (err: unknown): boolean => {
  if (!(err instanceof AxiosError) || err.response?.status !== 422)
    return false;
  const code = (err.response.data as { error?: string } | undefined)?.error;
  if (code === "name_taken" || code === "validation_failed") return true;
  const message = getErrorMessage(err).toLowerCase();
  return (
    message.includes("already") ||
    message.includes("taken") ||
    message.includes("exists")
  );
};

/** Order-independent deep equality for the plain-JSON view snapshots. */
const deepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (
    typeof a !== "object" ||
    typeof b !== "object" ||
    a === null ||
    b === null
  )
    return false;
  const aKeys = Object.keys(a as object);
  const bKeys = Object.keys(b as object);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every(
    (key) =>
      key in (b as object) &&
      deepEqual(
        (a as Record<string, unknown>)[key],
        (b as Record<string, unknown>)[key]
      )
  );
};

type ViewSnapshot = { filters: LeadViewFilters; columns: LeadViewColumns };

type NormalizedSnapshot = {
  filters: LeadViewFilters;
  columns: { id: string; checked: boolean }[];
};

/**
 * Canonicalize a snapshot so set-like filter arrays compare order-independently. Columns keep
 * their order: moving a column off a saved view marks the view as changed (CRM-1891).
 */
const normalizeSnapshot = (
  filters: LeadViewFilters,
  columns: ColumnVisibilityItem[]
): NormalizedSnapshot => ({
  filters: normalizeViewFilters(filters),
  columns: columns.map(({ id, checked }) => ({ id, checked }))
});

export type NameModalState =
  | { mode: "create" }
  | { mode: "rename"; view: LeadSavedView }
  | null;

interface ControllerParams {
  enabled: boolean;
  getSnapshot: () => ViewSnapshot;
  applyView: (view: LeadSavedView | null) => void;
  defaultSnapshot: ViewSnapshot;
  onViewSaved?: () => void;
  /** A saved view's columns are on screen: moving off them reads as a changed view. */
  isViewApplied?: boolean;
}

export const useLeadSavedViewsController = ({
  enabled,
  getSnapshot,
  applyView,
  defaultSnapshot,
  onViewSaved,
  isViewApplied = false
}: ControllerParams) => {
  const { views, isLoading, createView, updateView, deleteView } =
    useLeadSavedViews({ enabled });

  const [nameModal, setNameModal] = useState<NameModalState>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LeadSavedView | null>(null);

  // The active view is derived from the live filters + columns rather than
  // stored in transient state: that way it survives a page reload (the filters
  // persist in the URL/sessionStorage, so the matching saved view is recovered)
  // and stays in sync after apply/overwrite/create/delete without bookkeeping.
  const snapshot = useMemo(() => {
    const live = getSnapshot();
    return normalizeSnapshot(
      live.filters,
      live.columns[LEADS_COLUMNS_TAB_KEY] ?? []
    );
  }, [getSnapshot]);
  const defaultFilters = useMemo(
    () => normalizeViewFilters(defaultSnapshot.filters),
    [defaultSnapshot]
  );
  // Precompute each view's normalized snapshot once per `views`/default change,
  // so a keystroke-driven `snapshot` update doesn't re-normalize every saved
  // view on each render.
  const normalizedViews = useMemo(
    () =>
      views.map((view) => ({
        view,
        // Applying a view merges its columns with the defaults, so compare the
        // merged shape — else a view predating a column always reads "Custom".
        normalized: normalizeSnapshot(
          view.filters ?? defaultSnapshot.filters,
          getViewColumns(view, defaultSnapshot.columns[LEADS_COLUMNS_TAB_KEY])
        )
      })),
    [views, defaultSnapshot]
  );
  const activeView = useMemo(
    () =>
      normalizedViews.find(({ normalized }) => deepEqual(snapshot, normalized))
        ?.view ?? null,
    [normalizedViews, snapshot]
  );
  const activeViewId = activeView?.id ?? null;

  // "Custom" / dirty state: the live filters, sort or layout match neither a saved view nor
  // Default. Columns don't count against Default: Default keeps the user's own columns
  // (CRM-1891), so any column setup reads "Default" while filters, sort and layout match. While
  // a saved view is applied, its columns do count: moving or hiding one marks the view changed.
  const isModified =
    !activeView &&
    (isViewApplied || !deepEqual(snapshot.filters, defaultFilters));

  const handleApply = useCallback(
    (view: LeadSavedView | null) => {
      applyView(view);
    },
    [applyView]
  );

  const handleOverwrite = useCallback(
    (view: LeadSavedView) => {
      const { filters, columns } = getSnapshot();
      updateView.mutate(
        { id: view.id, filters, columns },
        {
          onSuccess: () => {
            onViewSaved?.();
            toast.success("View updated");
          },
          onError: (err) => toast.error(getErrorMessage(err))
        }
      );
    },
    [getSnapshot, updateView, onViewSaved]
  );

  const handleConfirmDelete = useCallback(() => {
    if (!deleteTarget) return;
    const { id } = deleteTarget;
    deleteView.mutate(id, {
      onSuccess: () => {
        setDeleteTarget(null);
        toast.success("View deleted");
      },
      onError: (err) => toast.error(getErrorMessage(err))
    });
  }, [deleteTarget, deleteView]);

  const handleSubmitName = useCallback(
    (name: string) => {
      if (!nameModal) return;
      setNameError(null);

      const onError = (err: unknown) => {
        if (isDuplicateNameError(err)) setNameError(DUPLICATE_NAME_MESSAGE);
        else toast.error(getErrorMessage(err));
      };

      if (nameModal.mode === "create") {
        const { filters, columns } = getSnapshot();
        createView.mutate(
          { name, filters, columns },
          {
            onSuccess: () => {
              setNameModal(null);
              onViewSaved?.();
              toast.success("View saved");
            },
            onError
          }
        );
        return;
      }

      updateView.mutate(
        { id: nameModal.view.id, name },
        {
          onSuccess: () => {
            setNameModal(null);
            toast.success("View renamed");
          },
          onError
        }
      );
    },
    [nameModal, getSnapshot, createView, updateView, onViewSaved]
  );

  const isNamePending =
    nameModal?.mode === "rename" ? updateView.isPending : createView.isPending;

  return {
    controlProps: {
      views,
      isLoading,
      activeViewId,
      isModified,
      isOverwriting: updateView.isPending,
      onApply: handleApply,
      onSaveCurrent: () => setNameModal({ mode: "create" }),
      onRename: (view: LeadSavedView) => {
        setNameError(null);
        setNameModal({ mode: "rename", view });
      },
      onOverwrite: handleOverwrite,
      onDelete: (view: LeadSavedView) => setDeleteTarget(view)
    },
    modalsProps: {
      nameModal,
      nameError,
      isNamePending,
      onCloseName: () => {
        setNameModal(null);
        setNameError(null);
      },
      onClearNameError: () => setNameError(null),
      onSubmitName: handleSubmitName,
      deleteTarget,
      isDeleting: deleteView.isPending,
      onCloseDelete: () => setDeleteTarget(null),
      onConfirmDelete: handleConfirmDelete
    }
  };
};

export type LeadSavedViewsController = ReturnType<
  typeof useLeadSavedViewsController
>;
